import AppContext from 'App/AppContext';
import { IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import InfoMessage from 'components/Notification/InfoMessage';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import {
  AuditCriteriaCompare,
  auditCriteriaCompareChanged,
  auditCriteriaCompareOnlyInCopy,
  auditCriteriaCompareOnlyInSource,
  AuditCriteriaCompareRow,
  auditCriteriaCompareSame,
} from 'models/auditCriteriaGenericSets';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCompareAuditCriteriaWithSource } from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateShort } from 'utils/datetime';

interface IAuditCriteriaCompareModalProps {
  entityType: number;
  entityId: number;
  entityName?: string;
  onClose: () => void;
}

/**
 * "Compare with the generic set" (PLAN-075, spec 4): an item's own copy beside the generic set it was copied from,
 * criterion by criterion. Only the differences are listed; the same criteria are counted.
 */
const AuditCriteriaCompareModal = (props: IAuditCriteriaCompareModalProps) => {
  const { t } = useTranslation(['criteria', 'translation']);
  const appContext = useContext(AppContext);

  const [compare, setCompare] = useState<AuditCriteriaCompare | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        setCompare(await apiCompareAuditCriteriaWithSource(props.entityType, props.entityId, accessToken));
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.entityType, props.entityId]);

  const getChangeText = (row: AuditCriteriaCompareRow): string => {
    switch (row.changeKind) {
      case auditCriteriaCompareChanged:
        return t('criteria:Generic.Compare.Changed');
      case auditCriteriaCompareOnlyInCopy:
        return t('criteria:Generic.Compare.OnlyInCopy');
      case auditCriteriaCompareOnlyInSource:
        return t('criteria:Generic.Compare.OnlyInSource');
      default:
        return t('criteria:Generic.Compare.Same');
    }
  };

  const differences = (compare?.rows ?? []).filter((row) => row.changeKind !== auditCriteriaCompareSame);
  const sameCount = (compare?.rows ?? []).length - differences.length;
  const sourceChanged =
    compare?.sourceChangedOn !== undefined &&
    compare.copiedOn !== undefined &&
    compare.sourceChangedOn.getTime() > compare.copiedOn.getTime();

  return (
    <GenericModal
      isOpen={true}
      isBlocking
      onClose={props.onClose}
      title={
        props.entityName
          ? `${t('criteria:Generic.Compare.Title')} - ${props.entityName}`
          : t('criteria:Generic.Compare.Title')
      }
      hideFooter={false}
      closeButtonText={t('criteria:Close')}
      width={900}
      maxWidth={'min(90vw, 900px)'}
      maxHeight={'85vh'}
    >
      {isLoading ? (
        <IPSpinner size={IPSpinnerSize.medium} />
      ) : (
        <IPStack tokens={globalStackTokensGapMedium} styles={{ root: { paddingTop: 12 } }}>
          <IPStack.Item>
            <InfoMessage
              message={
                sourceChanged
                  ? t('criteria:Generic.Compare.InfoChanged', {
                      copied: toLocaleDateShort(compare?.copiedOn),
                      changed: toLocaleDateShort(compare?.sourceChangedOn),
                      same: sameCount,
                    })
                  : t('criteria:Generic.Compare.Info', {
                      copied: toLocaleDateShort(compare?.copiedOn),
                      same: sameCount,
                    })
              }
            />
          </IPStack.Item>
          {differences.length === 0 ? (
            <IPStack.Item>
              <IPText>{t('criteria:Generic.Compare.NoDifferences')}</IPText>
            </IPStack.Item>
          ) : (
            differences.map((row) => (
              <IPStack.Item key={row.lineageKey}>
                <IPSeparator />
                <IPText block variant="mediumPlus">
                  {getChangeText(row)}
                </IPText>
                <IPStack horizontal tokens={globalStackTokensGapSmall}>
                  <IPStack.Item styles={{ root: { flexBasis: 0, flexGrow: 1, minWidth: 0 } }}>
                    <IPText block variant="small">
                      {t('criteria:Generic.Compare.ThisItem')}
                    </IPText>
                    <IPText block>{row.copy?.requirement ?? '-'}</IPText>
                  </IPStack.Item>
                  <IPStack.Item styles={{ root: { flexBasis: 0, flexGrow: 1, minWidth: 0 } }}>
                    <IPText block variant="small">
                      {t('criteria:Generic.Compare.GenericSet')}
                    </IPText>
                    <IPText block>{row.source?.requirement ?? '-'}</IPText>
                  </IPStack.Item>
                </IPStack>
              </IPStack.Item>
            ))
          )}
        </IPStack>
      )}
    </GenericModal>
  );
};

export default AuditCriteriaCompareModal;
